import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { 
  Fuel,
  TrendingDown,
  Truck,
  Calendar as CalendarIcon
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { API, formatCurrency, formatDate } from "@/lib/utils";

export const FuelVendorSummary = () => {
  const [vendorData, setVendorData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVendor, setSelectedVendor] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/fuel-vendor-summary`);
      setVendorData(res.data || []);
    } catch (error) {
      console.error("Error fetching fuel vendor data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const totalFuelExpense = vendorData.reduce((sum, v) => sum + v.total_amount, 0);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-24 bg-slate-200 rounded-sm animate-pulse" />
        <div className="h-64 bg-slate-200 rounded-sm animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="border border-slate-200 shadow-sm rounded-sm">
        <CardHeader className="border-b border-slate-100 pb-4">
          <CardTitle className="font-display font-bold text-lg tracking-tight flex items-center gap-2">
            <Fuel className="w-5 h-5 text-orange-600" />
            Fuel Vendor Summary - कहाँ से कितना तेल लिया
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          {/* Total Summary */}
          <div className="mb-6 p-4 bg-orange-50 rounded-sm border border-orange-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-orange-600" />
                <span className="text-sm font-semibold text-orange-700">कुल Fuel खर्चा</span>
              </div>
              <span className="font-mono font-bold text-xl text-orange-700">{formatCurrency(totalFuelExpense)}</span>
            </div>
          </div>

          {/* Vendor Cards */}
          {vendorData.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Fuel className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>Koi fuel expense nahi mila.</p>
              <p className="text-xs mt-1">A1 Transport mein "Tell (Fuel)" expense add karo with vendor name.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {vendorData.map((vendor) => (
                <Card 
                  key={vendor.vendor_name} 
                  className={`cursor-pointer transition-all hover:shadow-md ${
                    selectedVendor === vendor.vendor_name ? 'ring-2 ring-orange-500' : ''
                  }`}
                  onClick={() => setSelectedVendor(selectedVendor === vendor.vendor_name ? null : vendor.vendor_name)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-slate-700">{vendor.vendor_name}</span>
                      <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">
                        {vendor.transaction_count} transactions
                      </span>
                    </div>
                    <div className="font-mono font-bold text-xl text-orange-600">
                      {formatCurrency(vendor.total_amount)}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Vendor Detail Table */}
          {selectedVendor && (
            <div className="mt-6 border rounded-sm overflow-hidden">
              <div className="bg-orange-50 p-3 border-b">
                <h3 className="font-semibold text-orange-700 flex items-center gap-2">
                  <Fuel className="w-4 h-4" />
                  {selectedVendor} - Details
                </h3>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs">Date</TableHead>
                    <TableHead className="text-xs">Gari No.</TableHead>
                    <TableHead className="text-xs">Description</TableHead>
                    <TableHead className="text-xs text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vendorData
                    .find(v => v.vendor_name === selectedVendor)
                    ?.transactions.map((t, idx) => (
                      <TableRow key={t.id || idx}>
                        <TableCell className="text-xs">{formatDate(t.date)}</TableCell>
                        <TableCell className="text-xs font-mono">{t.vehicle_number || '-'}</TableCell>
                        <TableCell className="text-xs text-slate-600">{t.description || '-'}</TableCell>
                        <TableCell className="text-xs text-right font-mono font-semibold text-orange-600">
                          {formatCurrency(t.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
